import { Http } from '@mygpt/app/http';

// GBase Project 数据结构
export interface GbaseProject {
  uuid: string;
  title: string;
  web_url: string;
  item_count: number;
  my_role: string;
  created_at?: string;
}

// 授权状态响应
export interface GbaseAuthStatusResponse {
  dataset_id: string;
  gbase_integration: {
    authorized: boolean;
    has_projects: boolean;
    project_count: number;
    token_expires_at: string;
    last_authorized_at: string;
    projects: GbaseProject[];
  };
}

// 授权请求参数
export interface GbaseAuthorizeParams {
  dataset_id?: string; // 可选，如果在知识库设置页中发起授权，可以传知识库 UUID
}

// 授权响应
export interface GbaseAuthorizeResponse {
  authorization_url: string;
  state: string;
}

// 关联 Project 请求参数
export interface GbaseAssociateProjectsParams {
  dataset_id: string;
  project_uuids: string[];
}

// 获取 Project 列表请求参数
export interface GetGbaseProjectsParams {
  current_page?: number;
  page_size?: number;
  title_like?: string;
}

// 分页信息
export interface GbasePaginator {
  currentPage: number;
  items: number;
  pageSize: number;
  pages: number;
  startRow: number;
  endRow: number;
}

// 获取 Project 列表响应
export interface GetGbaseProjectsResponse {
  projects: GbaseProject[];
  paginator: GbasePaginator;
}

/**
 * 发起 GBase 授权
 * POST /api/auth/gbase/authorize
 */
export const gbaseAuthorize = (params: GbaseAuthorizeParams) => {
  const { post } = Http.use();
  return post<GbaseAuthorizeResponse>('/api/auth/gbase/authorize', params);
};

/**
 * 获取可用的 GBase Project 列表
 * GET /api/auth/gbase/projects
 */
export const getGbaseProjects = (params?: GetGbaseProjectsParams) => {
  const { get } = Http.use();
  const defaultParams: GetGbaseProjectsParams = {
    current_page: 1,
    page_size: 10,
    ...params,
  };
  return get<GetGbaseProjectsResponse>('/api/auth/gbase/projects', { params: defaultParams });
};

/**
 * 关联 Bot 和 GBase Project
 * POST /api/auth/gbase/associate-projects
 */
export const associateGbaseProjects = (params: GbaseAssociateProjectsParams) => {
  const { post } = Http.use();
  return post('/api/auth/gbase/associate-projects', params);
};

/**
 * 解除知识库和 GBase Project 的关联
 * DELETE /api/auth/gbase/dataset/{dataset_id}/projects/{project_uuid}
 */
export const disassociateGbaseProject = (datasetId: string, projectUuid: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/api/auth/gbase/dataset/${datasetId}/projects/${projectUuid}`);
};

/**
 * 获取知识库已关联的 GBase Project 列表
 * GET /api/auth/gbase/dataset/{dataset_id}/projects
 */
export const getDatasetGbaseProjects = (datasetId: string) => {
  const { get } = Http.use();
  return get<GbaseProject[]>(`/api/auth/gbase/dataset/${datasetId}/projects`);
};

/**
 * 获取 GBase 授权状态
 * GET /api/auth/gbase/dataset/{dataset_id}/projects/status
 */
export const getGbaseAuthStatus = (datasetId: string) => {
  const { get } = Http.use();
  return get<GbaseAuthStatusResponse>(`/api/auth/gbase/dataset/${datasetId}/projects/status`);
};

/**
 * 断开 GBase 授权连接
 * DELETE /api/auth/gbase/authorization
 */
export const disconnectGbaseAuthorization = (datasetId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp('/api/auth/gbase/authorization', { params: { dataset_id: datasetId } });
};
